import { useState, useEffect } from 'react'
import { Box, Flex, Text, ConfirmDialog, useT } from '@minetrace/ui'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormColors, WizardSidebar } from '@minetrace/ui/forms'
import {
  LuFingerprint as Fingerprint,
  LuShieldCheck as ShieldCheck,
  LuMountain as Mountain,
  LuFileText as FileText,
  LuFactory as Factory,
  LuPackage as Package,
  LuTriangleAlert as AlertTriangle,
  LuLandmark as Landmark,
  LuClipboardCheck as ClipboardCheck,
  LuFilePen as FileSignature,
  LuDownload as Download,
  LuUsers as Users,
} from 'react-icons/lu'
import { toaster } from '@minetrace/ui'
import { Spinner } from '@chakra-ui/react'
import { brand } from '../../theme'
import { formatDateTime } from '../../lib/dates'
import { proveedoresService } from '../../api/services'
import type { ProveedorResponse, ObservationResponse, StepApprovalEntry, StepApprovalStatus } from '../../api/models/response/proveedor'
import type { RecpoRecordResponse } from '../../api/models/response/consultarRuc'

// Step components
import Step1Identity from './steps/Step1Identity'
import PowerValiditiesSection from './PowerValiditiesSection'
import Step2Accreditation from './steps/Step2Accreditation'
import Step3MiningRights from './steps/Step3MiningRights'
import Step4Igafom from './steps/Step4Igafom'
import Step4bLegalDocuments from './steps/Step4bLegalDocuments'
import Step5Production from './steps/Step5Production'
import Step6Recpo from './steps/Step6Recpo'
import Step7Iqbf from './steps/Step7Iqbf'
import Step2bContrato from './steps/Step2bContrato'
import Step8Finance from './steps/Step8Finance'
import Step9Verification from './steps/Step9Verification'
import Step10Ubo from './steps/Step10Ubo'
import ObservationBox from '../../components/ObservationBox'
import { TAB_TO_APPROVAL_STEP } from '../../lib/supplierApprovalSteps'

type TabType = 'identidad' | 'acreditacion' | 'contrato' | 'derechos' | 'igafom' | 'legaldocs' | 'produccion' | 'recpo' | 'iqbf' | 'finanzas' | 'accionistas' | 'verificaciones'

// 'accionistas' solo aplica a sociedades (S.A./S.A.C.); se filtra del orden cuando
// el proveedor no es sociedad (ver TAB_ORDER dentro del componente).
const BASE_TAB_ORDER: TabType[] = ['identidad', 'acreditacion', 'contrato', 'derechos', 'igafom', 'legaldocs', 'produccion', 'recpo', 'iqbf', 'finanzas', 'accionistas', 'verificaciones']

// Cada pestaña es una unidad de aprobación independiente. `legaldocs` compartía el 5
// con `igafom`: observar un IGAFOM daba por observado también "Otros Documentos
// Legales" y aprobar uno aprobaba el otro. Son grupos distintos para el evaluador.
// Mismo mapa en registro y evaluación (ver lib/supplierApprovalSteps).
const TAB_TO_STEP: Record<TabType, number> = TAB_TO_APPROVAL_STEP

export default function EvaluarProveedorPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState<TabType>('identidad')
  const [loading, setLoading] = useState(true)
  const [proveedor, setProveedor] = useState<ProveedorResponse | null>(null)
  const [observations, setObservations] = useState<ObservationResponse[]>([])
  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const [stepApprovals, setStepApprovals] = useState<StepApprovalEntry[]>([])
  const [approvingStep, setApprovingStep] = useState<number | null>(null)
  const [confirmAuthorizeOpen, setConfirmAuthorizeOpen] = useState(false)

  // El tab de Accionistas (UBO) solo aplica a sociedades S.A./S.A.C.
  const isCorporation = proveedor?.isCorporation ?? false
  const TAB_ORDER = isCorporation ? BASE_TAB_ORDER : BASE_TAB_ORDER.filter(tab => tab !== 'accionistas')

  useEffect(() => {
    if (!id) return
    const load = async () => {
      setLoading(true)
      try {
        const data = await proveedoresService.getById(id)
        setProveedor(data)
        setObservations(data.observations ?? [])
        try {
          const approvals = await proveedoresService.getStepApprovals(id)
          setStepApprovals(approvals)
        } catch { /* */ }
      } catch { /* */ } finally { setLoading(false) }
    }
    load()
  }, [id])

  const handleApproveStep = async (step: number) => {
    if (!id) return
    setApprovingStep(step)
    try {
      await proveedoresService.approveStep(id, step)
      const fresh = await proveedoresService.getStepApprovals(id)
      setStepApprovals(fresh)
    } catch {
      toaster.error({ description: t('proveedores.wizard.evaluar.authorizeError') })
    } finally {
      setApprovingStep(null)
    }
  }

  // Tras observar/resolver cambian el estado del minero (Observado ↔ En evaluación) y el
  // estado de aprobación de los pasos (bloqueado por observación). Antes sólo se tocaba la
  // lista local: la insignia de estado y la barra lateral quedaban desactualizadas.
  const refreshEvaluation = async () => {
    if (!id) return
    try {
      const [data, approvals] = await Promise.all([proveedoresService.getById(id), proveedoresService.getStepApprovals(id)])
      setProveedor(data)
      setObservations(data.observations ?? [])
      setStepApprovals(approvals)
    } catch { /* la acción ya se guardó; el próximo load lo refleja */ }
  }

  const handleAddObservation = async (text: string) => {
    if (!id) return
    const result = await proveedoresService.addObservation(id, { step: TAB_TO_STEP[activeTab], observationText: text })
    setObservations(prev => [...prev, result])
    await refreshEvaluation()
  }

  const handleResolveObservation = async (obsId: string) => {
    if (!id) return
    await proveedoresService.resolveObservation(id, obsId)
    setObservations(prev => prev.map(o => o.id === obsId ? { ...o, resolvedAt: new Date().toISOString() } : o))
    await refreshEvaluation()
  }

  const handleObsReply = async (obsId: string, text: string) => {
    if (!id) return
    try {
      await proveedoresService.addObservationReply(id, obsId, text)
      // Re-fetch para traer la respuesta recién creada (con autor/fecha del servidor).
      const data = await proveedoresService.getById(id)
      setObservations(data.observations ?? [])
    } catch {
      toaster.error({ description: t('proveedores.wizard.evaluar.observationReplyError') })
    }
  }

  // Hilos de respuesta por observación, para el ObservationBox.
  const repliesByObs: Record<string, { id: string; replyText: string; createdByUserName: string | null; createdAt: string }[]> = {}
  for (const o of observations) {
    if (o.replies?.length) {
      repliesByObs[o.id] = o.replies.map(r => ({ id: r.id, replyText: r.replyText, createdByUserName: r.createdByUserName, createdAt: r.createdAt }))
    }
  }

  const handleAuthorize = async () => {
    if (!id) return
    setIsAuthorizing(true)
    try {
      const cuid = await proveedoresService.authorize(id)
      toaster.success({ description: t('proveedores.wizard.evaluar.authorizeSuccess', { cuid: cuid.cuidCode }) })
      setTimeout(() => navigate('/app/proveedores'), 2000)
    } catch {
      toaster.error({ description: t('proveedores.wizard.evaluar.authorizeError') })
    } finally { setIsAuthorizing(false) }
  }

  const pendingObservations = observations.filter(o => !o.resolvedAt)
  const allApproved =
    stepApprovals.length > 0 &&
    stepApprovals.every(e => e.status === 'approved')

  // Per-step approval state per tab
  const stepStatusByTab = (key: TabType): StepApprovalStatus | 'pending' => {
    const entry = stepApprovals.find(e => e.step === TAB_TO_STEP[key])
    return entry?.status ?? 'pending'
  }

  const stepPendingObsByTab = (key: TabType): number => {
    const stepNum = TAB_TO_STEP[key]
    return observations.filter(o => o.step === stepNum && !o.resolvedAt).length
  }

  const stepHasAnyObsByTab = (key: TabType): boolean => {
    const stepNum = TAB_TO_STEP[key]
    return observations.some(o => o.step === stepNum)
  }

  // Sidebar status precedence:
  //   approved (real approval) → observed (any unresolved obs) → inReview (touched but not yet approved)
  //   → pending (untouched).
  // Stale or blockedByObservation are always "observed/inReview" depending on observations.
  const sidebarStatusByTab = (key: TabType): 'approved' | 'observed' | 'inReview' | 'pending' => {
    const status = stepStatusByTab(key)
    const pending = stepPendingObsByTab(key)
    if (status === 'approved') return 'approved'
    if (pending > 0) return 'observed'
    if (status === 'stale' || status === 'blockedByObservation' || stepHasAnyObsByTab(key)) return 'inReview'
    return 'pending'
  }

  const STEP_ICONS: Record<string, React.ReactNode> = {
    identidad: <Fingerprint size={18} />, acreditacion: <ShieldCheck size={18} />, contrato: <FileSignature size={18} />, derechos: <Mountain size={18} />,
    igafom: <FileText size={18} />, legaldocs: <FileText size={18} />, produccion: <Factory size={18} />, recpo: <Package size={18} />,
    iqbf: <AlertTriangle size={18} />, finanzas: <Landmark size={18} />, accionistas: <Users size={18} />, verificaciones: <ClipboardCheck size={18} />,
  }

  const tabs = TAB_ORDER.map(key => {
    return {
      key,
      label: ({
        identidad: t('proveedores.wizard.tabs.identidad'),
        acreditacion: t('proveedores.wizard.tabs.acreditacion'),
        contrato: t('proveedores.wizard.tabs.contrato'),
        derechos: t('proveedores.wizard.tabs.derechos'),
        igafom: t('proveedores.wizard.tabs.igafom'),
        legaldocs: t('proveedores.wizard.tabs.legaldocs'),
        produccion: t('proveedores.wizard.tabs.produccion'),
        recpo: t('proveedores.wizard.tabs.recpo'),
        iqbf: t('proveedores.wizard.tabs.iqbf'),
        finanzas: t('proveedores.wizard.tabs.finanzas'),
        accionistas: t('proveedores.wizard.tabs.accionistas'),
        verificaciones: t('proveedores.wizard.tabs.verificaciones'),
      })[key],
      icon: STEP_ICONS[key],
      status: sidebarStatusByTab(key),
      observationCount: stepPendingObsByTab(key),
    }
  })

  const completedSteps = new Set<string>(TAB_ORDER.filter(tab => stepStatusByTab(tab) === 'approved'))

  if (loading || !proveedor) {
    return (
      <Box p={8} textAlign="center"><Text tid="proveedores.wizard.evaluar.loading" color={c.textMuted} /></Box>
    )
  }

  // Dummy no-op handlers for readonly mode
  // Vuelve a leer el minero sin el spinner de carga inicial: se usa después de traer
  // datos de INGEMMET, que es la única acción que esta pantalla de solo lectura permite
  // y que cambia lo que se está mirando. Sin esto el evaluador consultaba el registro
  // oficial y la ficha seguía mostrando lo anterior hasta recargar la página — incluido
  // el aviso de "el minero no es el titular" cuando ya se había resuelto que sí lo es.
  const recargarProveedor = async () => {
    if (!id) return
    try {
      setProveedor(await proveedoresService.getById(id))
    } catch { /* se conserva lo que ya estaba en pantalla */ }
  }

  const noop = async () => {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const noopReturn = async (): Promise<any> => ({} as unknown)

  const activeIndex = TAB_ORDER.indexOf(activeTab)

  return (
    <Flex mx={-8} mt={-4}>
      {/* Sidebar */}
      <WizardSidebar
        title={t('proveedores.wizard.evaluar.title')}
        subtitle={t('proveedores.wizard.evaluar.subtitle', { ruc: proveedor.ruc, current: activeIndex + 1, total: TAB_ORDER.length })}
        steps={tabs}
        activeStep={activeTab}
        completedSteps={completedSteps}
        onStepClick={setActiveTab}
      />

      {/* Main Content */}
      <Box flex={1} py={4} px={6} bg="white">
        <Box>
          {/* Header */}
          <Box mb={6}>
            <Flex alignItems="center" gap={3} mb={3}>
              <Box
                as="button" type="button" display="flex" alignItems="center" justifyContent="center"
                w="32px" h="32px" borderRadius="lg" color="#51634d" cursor="pointer" _hover={{ bg: '#ebefed' }}
                onClick={() => navigate('/app/proveedores')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
              </Box>
              <Box px={3} py={1} borderRadius="full" fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="wider"
                bg={proveedor.registrationStatus === 3 ? '#c1ecd4' : pendingObservations.length > 0 ? '#f9eccf' : '#e0e7ff'}
                color={proveedor.registrationStatus === 3 ? '#274e3d' : pendingObservations.length > 0 ? '#92400e' : '#3730a3'}
              >
                {proveedor.registrationStatus === 3 ? t('proveedores.wizard.evaluar.statusApproved') : pendingObservations.length > 0 ? t('proveedores.wizard.evaluar.statusObserved') : t('proveedores.wizard.evaluar.statusInReview')}
              </Box>
            </Flex>
            <Text fontSize="3xl" fontWeight="800" color="#012d1d" fontFamily="heading">{proveedor.legalName}</Text>
            <Text color="#414844" mt={2}>RUC: {proveedor.ruc}</Text>
          </Box>

          {/* Content Card */}
          <Box bg="white" borderRadius="xl" shadow="0 8px 32px rgba(0,0,0,0.04)" overflow="hidden" p={8}>

      {/* Step content — readonly */}
      {/* eslint-disable @typescript-eslint/no-explicit-any */}
      {activeTab === 'identidad' && (
        <Step1Identity
          mode="readonly"
          defaultValues={{
            ruc: proveedor.ruc, legalName: proveedor.legalName, commercialName: proveedor.commercialName ?? '',
            taxAddress: proveedor.taxAddress ?? '', ubigeoCode: proveedor.ubigeoCode ?? '',
            mainPhone: proveedor.mainPhone ?? '', mainEmail: proveedor.mainEmail ?? '',
            website: proveedor.website ?? '', prefix: proveedor.prefix ?? '',
          }}
          initialConsultaData={{
            economicActivities: proveedor.economicActivities ?? [],
            taxpayerStatus: proveedor.taxpayerStatus ?? null,
            taxpayerCondition: proveedor.taxpayerCondition ?? null,
            taxpayerType: proveedor.taxpayerType ?? null,
          }}
          companyId={proveedor.id} persons={proveedor.persons ?? []} onPersonsChange={noop as any}
          addPerson={noopReturn} removePerson={noop}
        />
      )}
      {/* Vigencia de poder del representante legal (personas jurídicas): el evaluador
          tiene que poder verla y descargarla para revisar el paso Identidad. */}
      {activeTab === 'identidad' && (
        <PowerValiditiesSection
          mode="readonly"
          ruc={proveedor.ruc}
          persons={proveedor.persons ?? []}
          powerValidities={proveedor.powerValidities ?? []}
        />
      )}

      {activeTab === 'acreditacion' && (
        <Step2Accreditation
          mode="readonly"
          defaultCategoryId={proveedor.miningSupplier?.categoryId ?? undefined}
        />
      )}

      {activeTab === 'contrato' && (
        <Step2bContrato
          mode="readonly"
          activeContract={proveedor.activeContract ?? null}
          contractHistory={proveedor.contractHistory ?? []}
          onUpload={noop}
        />
      )}

      {activeTab === 'derechos' && (
        <Step3MiningRights
          mode="readonly" companyId={proveedor.id} savedMiningRights={proveedor.miningRights ?? []}
          onSavePolygon={noop as any} onAddComponent={noopReturn} onRemoveComponent={noop}
          onRefresh={recargarProveedor}
        />
      )}

      {activeTab === 'igafom' && (
        <Step4Igafom mode="readonly" companyId={proveedor.id} savedMiningRights={proveedor.miningRights ?? []} minerSource={proveedor.legalCondition === 1 ? 'FormalizedMiner' : proveedor.legalCondition === 2 ? 'Reinfo' : null} onAdd={noop} onUpdate={noop} onRemove={noop} />
      )}

      {activeTab === 'legaldocs' && (
        <Step4bLegalDocuments mode="readonly" companyId={proveedor.id} savedMiningRights={proveedor.miningRights ?? []} onAdd={noop} onUpdate={noop} onRemove={noop} />
      )}

      {activeTab === 'produccion' && (
        <Step5Production mode="readonly" companyId={proveedor.id} savedMiningRights={proveedor.miningRights ?? []} onAdd={noop} onRemove={noop} />
      )}

      {activeTab === 'recpo' && (
        <Step6Recpo mode="readonly" recpoRecords={proveedor.recpoRecords as RecpoRecordResponse[] ?? []} />
      )}

      {activeTab === 'iqbf' && (
        <Step7Iqbf
          mode="readonly" companyId={proveedor.id} savedMiningRights={proveedor.miningRights ?? []}
          onToggleUsesExplosives={noop} onAddAuthorization={noop} onUpdateAuthorization={noop} onRemoveAuthorization={noop}
          onAddHandler={noop} onBulkAddHandlers={noop} onRemoveHandler={noop}
        />
      )}

      {activeTab === 'finanzas' && (
        <Step8Finance
          mode="readonly" bankAccounts={proveedor.bankAccounts ?? []}
          onAddAccount={noopReturn} onRemoveAccount={noop}
          incomeTaxCategoryId={proveedor.miningSupplier?.incomeTaxCategoryId ?? 0}
          onIncomeTaxChange={noop as any}
          defaultSplaftDeclared={proveedor.splaftDeclared ?? false}
          // Sin estos dos, la pantalla de evaluacion decia "no se ha cargado ningun
          // reporte tributario" y no mostraba la declaracion SPLAFT firmada aunque
          // estuvieran cargadas: son props opcionales y esta vista no las pasaba.
          // El evaluador observaba por falta de documentos que si estaban.
          splaftDocumentFile={proveedor.splaftDocumentFile}
          taxReports={proveedor.taxReports ?? []}
        />
      )}

      {activeTab === 'accionistas' && (
        <Step10Ubo
          isReadonly
          shareholders={proveedor.shareholders ?? []}
          requiresEnhancedDueDiligence={proveedor.requiresEnhancedDueDiligence ?? false}
          onAddFromCertificate={noop} onUpdate={noop} onRemove={noop}
        />
      )}

      {/* eslint-enable @typescript-eslint/no-explicit-any */}
      {activeTab === 'verificaciones' && (
        <Step9Verification companyId={proveedor.id} />
      )}

      {/* Diagnostic + observations + approval for current tab */}
      {(() => {
        const stepNum = TAB_TO_STEP[activeTab]
        const entry = stepApprovals.find(e => e.step === stepNum) ?? null
        return (
          <ObservationBox
            // Una instancia por paso: sin key React reutilizaba la misma y el texto sin
            // enviar y el modo Conforme/Observado pasaban de un tab a otro.
            key={activeTab}
            step={stepNum}
            observations={observations}
            approvalEntry={entry}
            isApproving={approvingStep === stepNum}
            onAdd={handleAddObservation}
            onResolve={handleResolveObservation}
            onApprove={() => handleApproveStep(stepNum)}
            repliesByObs={repliesByObs}
            onReply={handleObsReply}
          />
        )
      })()}

      {/* Authorize button */}
      {proveedor.registrationStatus !== 3 && (
        <Flex justifyContent="center" py={4}>
          <Box
            as="button" type="button" display="flex" alignItems="center" gap={2} px={8} h="48px"
            bg={allApproved ? '#1d6664' : '#b5aa9d'} color="white" borderRadius="xl" fontSize="md" fontWeight="700"
            cursor={allApproved && !isAuthorizing ? 'pointer' : 'not-allowed'}
            opacity={isAuthorizing ? 0.6 : 1}
            _hover={allApproved ? { bg: '#085a30' } : {}}
            onClick={allApproved && !isAuthorizing ? () => setConfirmAuthorizeOpen(true) : undefined}
            disabled={!allApproved || isAuthorizing}
          >
            {isAuthorizing ? t('proveedores.wizard.evaluar.authorizing') : t('proveedores.wizard.evaluar.authorizeButton')}
          </Box>
          {!allApproved && (
            <Text tid="proveedores.wizard.evaluar.stepsPendingTooltip" fontSize="xs" color="#a8740e" ml={3} alignSelf="center" />
          )}
        </Flex>
      )}

      {/* CUID display */}
      {proveedor.cuid && (
        <Box p={6} bg="#eef7f6" borderRadius="xl" border="1px solid" borderColor="#bfe3df" textAlign="center">
          <Text tid="proveedores.wizard.evaluar.cuidTitle" fontSize="lg" fontWeight="700" color="#1d6664" mb={2} />
          <Text fontSize="2xl" fontWeight="700" fontFamily="monospace" color="#1d6664">{proveedor.cuid.cuidCode}</Text>
          <Flex gap={4} justifyContent="center" mt={2} flexWrap="wrap">
            <Text tid="proveedores.wizard.evaluar.cuidAuthorizedAt" values={{ date: formatDateTime(proveedor.cuid.authorizedAt) }} fontSize="xs" color={c.textMuted} />
            <Text fontSize="xs" color={c.textMuted}>Version {proveedor.cuid.version} - Vence {formatDateTime(proveedor.cuid.expiresAt)}</Text>
          </Flex>
          <Flex gap={3} justifyContent="center" mt={4} flexWrap="wrap">
            {proveedor.cuid.certificateUrl && (
              <Box
                as="button" type="button"
                display="inline-flex"
                alignItems="center"
                gap={2}
                px={6} py={3}
                bg="#012d1d" color="white"
                borderRadius="md" fontSize="sm" fontWeight="700"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                onClick={() => proveedoresService.downloadCertificate(proveedor.cuid!.certificateUrl!)}
              >
                <Download size={16} />
                {t('proveedores.wizard.evaluar.downloadCertificate')}
              </Box>
            )}
            {proveedor.cuid.expedienteUrl && (
              <Box
                as="button" type="button"
                display="inline-flex"
                alignItems="center"
                gap={2}
                px={6} py={3}
                bg="#b4532a" color="white"
                borderRadius="md" fontSize="sm" fontWeight="700"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                onClick={() => proveedoresService.downloadExpediente(proveedor.cuid!).catch(() => toaster.error({ description: 'No se pudo descargar el expediente' }))}
              >
                <Download size={16} />
                Descargar expediente
              </Box>
            )}
          </Flex>
        </Box>
      )}
          </Box>
        </Box>
      </Box>

      {/* Confirm Authorize modal */}
      <ConfirmDialog
        open={confirmAuthorizeOpen}
        onClose={() => setConfirmAuthorizeOpen(false)}
        onConfirm={async () => {
          // Se cierra ANTES de empezar. Cerrarlo al terminar dejaba el dialogo encima del
          // aviso bloqueante durante los ~13 segundos que tarda la emision, tapandolo y
          // siguiendo a la vista con su boton "Si, aprobar": justo la pantalla ambigua que
          // el aviso venia a resolver.
          setConfirmAuthorizeOpen(false)
          await handleAuthorize()
        }}
        title={t('proveedores.wizard.evaluar.confirmApproveModal.title')}
        confirmLabel={t('proveedores.wizard.evaluar.confirmApproveModal.confirm')}
        cancelLabel={t('proveedores.wizard.evaluar.confirmApproveModal.cancel')}
        variant="default"
        isLoading={isAuthorizing}
      >
        {t('proveedores.wizard.evaluar.confirmApproveModal.body')}
      </ConfirmDialog>

      {/*
        Aviso bloqueante mientras se autoriza. Autorizar es lento de verdad: construye el
        snapshot del proveedor, genera el PDF del certificado y lo sube, inicializa el
        expediente subiendo un archivo al bucket POR CADA item, y despues del commit sella
        el indice digital con Cloud KMS. Escala con la cantidad de documentos del minero.
        Antes solo se veia un boton gris que decia "Autorizando...", y el evaluador no
        sabia si el sistema estaba trabajando o colgado. Observacion 3 de sep 2026.

        Es un overlay plano y no un Modal a proposito: no tiene que poder cerrarse, y
        montar un Modal para abrirlo en el mismo frame dispara el freeze de scroll-lock
        conocido del proyecto.
      */}
      {isAuthorizing && (
        <Box
          position="fixed"
          inset={0}
          zIndex={2000}
          bg="rgba(0, 0, 0, 0.45)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
        >
          <Box bg="white" borderRadius="xl" p={6} maxW="440px" boxShadow="0 10px 40px rgba(0,0,0,0.2)">
            <Flex gap={3} alignItems="center" mb={3}>
              <Spinner size="md" color={brand.green} />
              <Text fontSize="md" fontWeight="700" color="#2a2622">
                {t('proveedores.wizard.evaluar.authorizingModalTitle')}
              </Text>
            </Flex>
            <Text fontSize="sm" color="#4a5568" lineHeight="1.55">
              {t('proveedores.wizard.evaluar.authorizingModalBody', { name: proveedor.legalName })}
            </Text>
          </Box>
        </Box>
      )}
    </Flex>
  )
}
